import { useEffect, useState, type FormEvent } from 'react';
import { SERVICE_KINDS, SERVICE_UNITS, centsToPesos, pesosToCents, type ServiceKind, type ServiceUnitName } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  Modal,
  Spinner,
  inputCls,
} from '../../components/ui';
import { Icon } from '../../components/Icon';

interface ServiceDto {
  id: string;
  branchId: string;
  branchCode?: string;
  name: string;
  kind: ServiceKind;
  unit: ServiceUnitName;
  priceCents: number;
  minKilos: number | null;
  isActive: boolean;
}

const autoWidthCls = inputCls.replace('w-full', '');

function unitSuffix(unit: string): string {
  return unit === 'KG' ? '/kg' : unit === 'PIECE' ? '/pc' : '';
}

function ServiceFormModal({
  initial,
  defaultBranchId,
  onClose,
  onSaved,
}: {
  initial?: ServiceDto;
  defaultBranchId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { branches } = useAuth();
  const isEdit = Boolean(initial);
  const [branchId, setBranchId] = useState(initial?.branchId ?? defaultBranchId);
  const [name, setName] = useState(initial?.name ?? '');
  const [kind, setKind] = useState<ServiceKind>(initial?.kind ?? 'MAIN');
  const [unit, setUnit] = useState<ServiceUnitName>(initial?.unit ?? 'KG');
  const [price, setPrice] = useState(initial ? (initial.priceCents / 100).toString() : '');
  const [minKilos, setMinKilos] = useState(initial?.minKilos != null ? String(initial.minKilos) : '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrors({});
    setFormError(null);

    const payload: Record<string, unknown> = {
      name: name.trim(),
      kind,
      unit,
      priceCents: pesosToCents(Number(price)),
      // Only flat-rate primary services can carry a minimum load; blank
      // clears it (the API validates the final catalogue state).
      minKilos: kind === 'MAIN' && unit === 'FLAT' && minKilos.trim() ? Number(minKilos) : null,
    };

    try {
      if (isEdit && initial) {
        await api.patch(`/services/${initial.id}`, payload);
      } else {
        await api.post('/services', { ...payload, branchId });
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const fields: Record<string, string> = {};
        if (Array.isArray(err.details)) {
          for (const detail of err.details) {
            if (detail && typeof detail === 'object' && 'path' in detail) {
              fields[String((detail as { path: unknown }).path)] = String(
                (detail as { message: unknown }).message,
              );
            }
          }
        }
        if (err.status === 409) fields.name = err.message;
        setErrors(fields);
        if (Object.keys(fields).length === 0) setFormError(err.message);
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit service' : 'New service'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={(e) => void doSubmit(e)} disabled={busy}>
            {busy ? <Spinner className="h-4 w-4" /> : null}
            {isEdit ? 'Save changes' : 'Create service'}
          </Button>
        </>
      }
    >
      <form onSubmit={doSubmit} className="space-y-4">
        {formError && <ErrorBanner message={formError} />}
        {!isEdit && (
          <Field label="Branch" htmlFor="svc-branch" error={errors.branchId}>
            <select
              id="svc-branch"
              className={inputCls}
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Name" htmlFor="svc-name" error={errors.name}>
          <input
            id="svc-name"
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wash-Dry-Fold"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type" htmlFor="svc-kind" error={errors.kind}>
            <select
              id="svc-kind"
              className={inputCls}
              value={kind}
              onChange={(e) => {
                const next = e.target.value as ServiceKind;
                setKind(next);
                if (next === 'ADDON') setUnit('FLAT');
              }}
            >
              {SERVICE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k === 'MAIN' ? 'Primary service' : 'Additional service'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priced by" htmlFor="svc-unit" error={errors.unit}>
            <select
              id="svc-unit"
              className={inputCls}
              value={unit}
              onChange={(e) => setUnit(e.target.value as ServiceUnitName)}
              disabled={kind === 'ADDON'}
            >
              {SERVICE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u === 'KG' ? 'Per kilo' : u === 'PIECE' ? 'Per item' : 'Flat rate'}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {kind === 'MAIN' && unit === 'FLAT' && (
          <Field
            label="Minimum load (kg)"
            htmlFor="svc-min-kilos"
            error={errors.minKilos}
            hint="Optional — the flat price covers this minimum weight"
          >
            <input
              id="svc-min-kilos"
              className={inputCls}
              value={minKilos}
              onChange={(e) => setMinKilos(e.target.value)}
              inputMode="numeric"
              type="number"
              min={1}
              step={1}
              placeholder="e.g. 8"
            />
          </Field>
        )}
        <Field
          label="Price (₱)"
          htmlFor="svc-price"
          error={errors.priceCents}
          hint={kind === 'MAIN' ? 'Charged once per transaction' : 'Charged per quantity selected'}
        >
          <input
            id="svc-price"
            className={inputCls}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            type="number"
            step="0.01"
            min={0}
            placeholder="e.g. 50.00"
            required
          />
        </Field>
        {isEdit && (
          <p className="rounded-xl bg-indigo-50 px-3 py-2.5 text-sm text-indigo-700">
            Price changes never alter past transactions — every transaction stores its own price
            snapshot.
          </p>
        )}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

/**
 * Settings > Services & pricing (admin, pricing.manage).
 * Branch-aware catalog: each branch owns its rows, so the same service may
 * carry a different price per branch.
 */
export function ServicesSection() {
  const { branchFilter } = useAuth();
  const [services, setServices] = useState<ServiceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ServiceDto | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ includeInactive: 'true' });
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);

    void api
      .get<{ services: ServiceDto[] }>(`/services?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setServices(res.services);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load services.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <section aria-label="Services and pricing" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Services & pricing</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Branch-aware price list used by receiving. Price changes are audit-logged.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Icon name="plus" className="h-5 w-5" />
          New service
        </Button>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-6 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading services…
        </div>
      ) : services.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">
          No services yet. Add one to start receiving transactions.
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {services.map((svc) => (
            <Card key={svc.id} className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-semibold text-slate-900">{svc.name}</p>
                  <Badge>{svc.branchCode ?? '—'}</Badge>
                  {svc.kind === 'ADDON' && <Badge tone="indigo">Add-on</Badge>}
                  {!svc.isActive && <Badge tone="rose">Inactive</Badge>}
                </div>
                <p className="mt-1 text-sm font-bold text-indigo-700">
                  {centsToPesos(svc.priceCents)}
                  {unitSuffix(svc.unit)}
                  {svc.minKilos != null && (
                    <span className="ml-2 text-xs font-medium text-slate-500">
                      minimum {svc.minKilos} kg
                    </span>
                  )}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setEditing(svc)}>
                Edit
              </Button>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <ServiceFormModal
          defaultBranchId={services[0]?.branchId ?? ''}
          onClose={() => setCreating(false)}
          onSaved={refresh}
        />
      )}
      {editing && (
        <ServiceFormModal
          initial={editing}
          defaultBranchId={editing.branchId}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </section>
  );
}
